import {
  Children,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SyntheticEvent,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  autoUpdate,
  flip,
  offset as floatingOffset,
  safePolygon,
  size as floatingSize,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  XIcon,
} from "lucide-react";

import { cx, styled } from "simplydo/react/styled";
import { Icon, IconData, isIconData } from "simplydo/react/ui/icon";
import { Label, LabelContent, LabelDetail } from "simplydo/react/ui/label";
import { isElementOfType } from "simplydo/react/ui/utils";
import { getDefaultMountNode } from "simplydo/react/ui/utils/mountNode";

import {
  StyledDropdown,
  StyledDropdownLabelRemove,
  StyledDropdownMessage,
  StyledDropdownSearch,
  StyledDropdownText,
} from "./Dropdown.styles";
import type {
  DropdownDirection,
  DropdownOption,
  DropdownProps,
  DropdownValue,
  DropdownValueOrArray,
} from "./Dropdown.types";
import { DropdownContext } from "./DropdownContext";
import { DropdownDivider } from "./DropdownDivider";
import { DropdownItem } from "./DropdownItem";
import { DropdownMenu } from "./DropdownMenu";

type Placement =
  | "bottom-start"
  | "bottom-end"
  | "top-start"
  | "top-end"
  | "right-start"
  | "left-start";

function isOption(
  option: DropdownOption | null | undefined,
): option is DropdownOption {
  return Boolean(option);
}

function valuesEqual(a: unknown, b: unknown): boolean {
  return a === b || String(a) === String(b);
}

function toArray(
  value: DropdownValueOrArray | undefined | null,
): DropdownValue[] {
  if (value == null || value === undefined || value === "") {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function optionText(option: DropdownOption): string {
  if (typeof option.text === "string" || typeof option.text === "number") {
    return String(option.text);
  }
  if (option.value != null) {
    return String(option.value);
  }
  return "";
}

function optionDisplay(option: DropdownOption): ReactNode {
  // Prefer custom option children (e.g. rich selected rows) over plain text.
  return option.children ?? option.text ?? String(option.value);
}

function renderOptionIcon(icon: DropdownOption["icon"]): ReactNode {
  if (!icon) {
    return null;
  }
  if (isIconData(icon)) {
    return <Icon icon={icon as IconData} />;
  }
  return icon as unknown as ReactNode;
}

function optionChildren(option: DropdownOption): ReactNode {
  if (option.children !== undefined) {
    return option.children;
  }
  return (
    <>
      {renderOptionIcon(option.icon)}
      {option.text}
    </>
  );
}

function isSelectableOption(option: DropdownOption | undefined): boolean {
  return Boolean(option && !option.divider && !option.disabled);
}

function findNextSelectableIndex(
  options: DropdownOption[],
  from: number,
  direction: 1 | -1,
): number {
  if (options.length === 0) {
    return -1;
  }
  const start = from < 0 ? (direction === 1 ? -1 : 0) : from;
  for (let i = 0; i < options.length; i += 1) {
    const index =
      (start + direction * (i + 1) + options.length * 2) % options.length;
    if (isSelectableOption(options[index])) {
      return index;
    }
  }
  return -1;
}

function defaultFilter(
  options: DropdownOption[],
  query: string,
): DropdownOption[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return options;
  }
  return options.filter((opt) => {
    if (opt.divider) {
      return false;
    }
    const hay = `${optionText(opt)} ${opt.value ?? ""}`.toLowerCase();
    return hay.includes(q);
  });
}

function preferredPlacement(
  direction: DropdownDirection | undefined,
  upward: boolean | undefined,
  isNestedItemTrigger: boolean,
): Placement {
  const dir = direction ?? "right";
  if (isNestedItemTrigger) {
    return dir === "left" ? "left-start" : "right-start";
  }
  const side = upward ? "top" : "bottom";
  const align = dir === "left" ? "end" : "start";
  return `${side}-${align}`;
}

function computedFromPlacement(placement: string): {
  direction: DropdownDirection;
  upward: boolean;
} {
  if (placement.startsWith("left")) {
    return { direction: "left", upward: false };
  }
  if (placement.startsWith("right")) {
    return { direction: "right", upward: false };
  }
  return {
    direction: placement.endsWith("end") ? "left" : "right",
    upward: placement.startsWith("top"),
  };
}

/** Caret direction for nested item-trigger rows (matches submenu placement). */
function submenuCaretIcon(
  direction: DropdownDirection,
  upward: boolean,
): IconData {
  if (upward) {
    return ChevronUpIcon;
  }
  if (direction === "left") {
    return ChevronLeftIcon;
  }
  return ChevronRightIcon;
}

function isMenuElement(child: ReactNode): child is ReactElement {
  return isElementOfType(child, DropdownMenu);
}

function isItemTrigger(node: ReactNode): boolean {
  return isElementOfType(node, DropdownItem);
}

type MenuOpenEvent = { nodeId: string | undefined; parentId: string | null };

type DropdownComponentProps = DropdownProps & {
  /** FormField pass-through; ignored so it does not hit the DOM. */
  error?: boolean;
  ref?: Ref<HTMLElement>;
};

const DropdownComponent = (props: DropdownComponentProps): ReactElement => {
  const {
    allowAdditions,
    children,
    className,
    clearable,
    defaultSearchQuery,
    direction: directionProp,
    disabled,
    error: _error,
    fluid,
    loading,
    multiple: multipleProp = false,
    noResultsMessage = "No results found.",
    on = "click",
    onBlur,
    onChange,
    onClick,
    onFocus,
    onSearchChange,
    onKeyDown,
    options: optionsProp,
    placeholder,
    ref: propsRef,
    search: searchProp,
    searchInput,
    searchQuery: searchQueryProp,
    size,
    style,
    tabIndex,
    trigger,
    upward: upwardProp,
    value,
    variant = "inline",
    ...rest
  } = props;

  const nodeId = useFloatingNodeId();
  const parentId = useFloatingParentNodeId();
  const tree = useFloatingTree();

  const isOutline = variant === "outline";
  const hasOptions = Array.isArray(optionsProp);
  const search = searchProp || undefined;
  const isMultiple = Boolean(multipleProp);
  const isCombo = multipleProp === "combo";
  const closeOnChange = !isMultiple;

  const [internalOpen, setInternalOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState(
    searchQueryProp ?? defaultSearchQuery ?? "",
  );
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [keyboardSelection, setKeyboardSelection] =
    useState<DropdownValue | null>(null);

  const isSearchControlled = searchQueryProp !== undefined;

  const isOpen = disabled ? false : internalOpen;
  const searchQuery = isSearchControlled
    ? (searchQueryProp ?? "")
    : internalSearch;

  const searchInputRef = useRef<HTMLInputElement>(null);

  const setOpen = useCallback(
    (next: boolean, event?: SyntheticEvent) => {
      if (disabled) {
        return;
      }
      setInternalOpen(next);
      if (!next) {
        if (!isSearchControlled) {
          setInternalSearch("");
        }
        setHighlightedIndex(-1);
        setKeyboardSelection(null);
      }
    },
    [disabled, isSearchControlled],
  );

  // Sibling menus share a FloatingTree parent; opening one closes the others.
  // A leaf item click emits "click" so every ancestor menu closes too.
  useEffect(() => {
    if (!tree) {
      return;
    }
    const onMenuOpen = (event: MenuOpenEvent) => {
      if (event.nodeId !== nodeId && event.parentId === parentId && isOpen) {
        setOpen(false);
      }
    };
    const onTreeClick = () => {
      setOpen(false);
    };
    tree.events.on("menuopen", onMenuOpen);
    tree.events.on("click", onTreeClick);
    return () => {
      tree.events.off("menuopen", onMenuOpen);
      tree.events.off("click", onTreeClick);
    };
  }, [tree, nodeId, parentId, isOpen, setOpen]);

  useEffect(() => {
    if (isOpen && tree) {
      tree.events.emit("menuopen", { parentId, nodeId });
    }
  }, [tree, isOpen, nodeId, parentId]);

  const commitValue = useCallback(
    (
      event: SyntheticEvent<HTMLElement>,
      next: DropdownValueOrArray | undefined,
    ) => {
      onChange?.(event, { ...props, value: next });
    },
    [onChange, props],
  );

  const selectOption = useCallback(
    (
      event: SyntheticEvent<HTMLElement>,
      optionValue: DropdownValueOrArray | undefined,
    ) => {
      if (isMultiple) {
        const current = toArray(value);
        const single = Array.isArray(optionValue)
          ? optionValue[0]
          : optionValue;
        if (single === undefined) {
          commitValue(event, []);
        } else if (current.some((v) => valuesEqual(v, single))) {
          commitValue(
            event,
            current.filter((v) => !valuesEqual(v, single)),
          );
        } else {
          commitValue(event, [...current, single]);
        }
      } else {
        commitValue(event, optionValue);
      }

      if (!isSearchControlled) {
        setInternalSearch("");
      }

      if (closeOnChange) {
        setOpen(false, event);
      }
    },
    [
      closeOnChange,
      commitValue,
      isMultiple,
      isSearchControlled,
      setOpen,
      value,
    ],
  );

  const normalizedOptions = useMemo((): DropdownOption[] => {
    return (optionsProp || []).filter(isOption).map((option) => {
      const optionValue =
        option.value !== undefined && option.value !== null
          ? option.value
          : (option.key as DropdownValue | undefined);
      const key =
        option.key !== undefined && option.key !== null
          ? option.key
          : (optionValue as string | number | undefined);
      return {
        ...option,
        key:
          key === undefined
            ? undefined
            : typeof key === "boolean"
              ? String(key)
              : key,
        value: optionValue,
      };
    });
  }, [optionsProp]);

  const filteredOptions = useMemo(() => {
    if (!search) {
      return normalizedOptions;
    }
    if (typeof search === "function") {
      return search(normalizedOptions, searchQuery);
    }
    return defaultFilter(normalizedOptions, searchQuery);
  }, [normalizedOptions, search, searchQuery]);

  const additionOption = useMemo((): DropdownOption | null => {
    if (!allowAdditions || !search || !searchQuery.trim()) {
      return null;
    }
    const exists = normalizedOptions.some(
      (o) =>
        valuesEqual(o.value, searchQuery) ||
        optionText(o).toLowerCase() === searchQuery.trim().toLowerCase(),
    );
    if (exists) {
      return null;
    }
    return {
      key: `__addition_${searchQuery}`,
      value: searchQuery,
      text: `Add ${searchQuery}`,
    };
  }, [allowAdditions, normalizedOptions, search, searchQuery]);

  const visibleOptions = useMemo(() => {
    if (!additionOption) {
      return filteredOptions;
    }
    return [...filteredOptions, additionOption];
  }, [additionOption, filteredOptions]);

  const childrenArray = Children.toArray(children);
  const hasMenuChild = childrenArray.some(isMenuElement);
  // Custom menu children take precedence over options.
  const useChildrenMenu = hasMenuChild || (!hasOptions && children != null);

  const itemTrigger = isItemTrigger(trigger);
  const placement = preferredPlacement(directionProp, upwardProp, itemTrigger);

  const {
    refs,
    floatingStyles,
    context,
    placement: finalPlacement,
  } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: (next, event) => {
      setOpen(next, event as unknown as SyntheticEvent);
    },
    placement,
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      floatingOffset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      floatingSize({
        padding: 8,
        apply({ availableHeight, rects, elements }) {
          const maxHeight = `${Math.max(0, availableHeight)}px`;
          Object.assign(elements.floating.style, {
            minWidth: `${Math.max(rects.reference.width, 120)}px`,
            maxHeight,
          });
          // Menu owns overflow; set px height ( % max-height needs a definite parent height).
          const menu = elements.floating.firstElementChild;
          if (menu instanceof HTMLElement) {
            menu.style.maxHeight = maxHeight;
          }
        },
      }),
    ],
  });

  const computed = computedFromPlacement(finalPlacement ?? placement);

  // Floating UI owns all reference interactions. Search dropdowns only open on
  // click, while submenu mouse clicks are ignored in favour of hover (touch and
  // keyboard activation still work). Nested item-triggers always hover.
  const enableClick = !disabled && (on === "click" || itemTrigger);
  const enableHover = !disabled && (on === "hover" || itemTrigger);
  const click = useClick(context, {
    enabled: enableClick,
    toggle: !search && !itemTrigger,
    ignoreMouse: itemTrigger,
    keyboardHandlers: !search,
  });
  const hover = useHover(context, {
    enabled: enableHover,
    handleClose: safePolygon(),
    delay: { open: 75, close: 100 },
  });
  const dismiss = useDismiss(context, { enabled: !disabled, escapeKey: true });
  const role = useRole(context, { role: search ? "combobox" : "select" });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, hover, dismiss, role],
  );

  const triggerChildRef = isValidElement(trigger)
    ? ((trigger as ReactElement & { ref?: Ref<Element> }).ref ?? null)
    : null;
  // Attach Floating UI directly to the supplied trigger when there is one;
  // DropdownItem forwards this ref just like any other trigger component.
  const triggerReferenceRef = useMergeRefs([
    refs.setReference,
    triggerChildRef,
  ]);
  const rootReferenceRef = useMergeRefs([
    trigger ? null : refs.setReference,
    propsRef ?? null,
  ]);

  const selectedOptions = useMemo(() => {
    const selected = toArray(value);
    return normalizedOptions.filter((o) =>
      selected.some((v) => valuesEqual(v, o.value)),
    );
  }, [normalizedOptions, value]);

  const displayText = useMemo(() => {
    if (isCombo) {
      return null;
    }
    if (isMultiple) {
      if (selectedOptions.length === 0) {
        return null;
      }
      return selectedOptions.map(optionText).join(", ");
    }
    const selected = selectedOptions[0];
    if (selected) {
      return optionDisplay(selected);
    }
    return null;
  }, [isCombo, isMultiple, selectedOptions]);

  const showPlaceholder =
    !displayText &&
    (!isMultiple || toArray(value).length === 0) &&
    Boolean(placeholder);

  const hideTextForSearch = Boolean(search && isOpen && !!searchQuery);

  useEffect(() => {
    if (!search || !searchInputRef.current) {
      return;
    }
    if (isOpen) {
      searchInputRef.current.focus();
    } else {
      searchInputRef.current.blur();
    }
  }, [isOpen, search]);

  const clearValue = (event: SyntheticEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    commitValue(event, isMultiple ? [] : undefined);
    setKeyboardSelection(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (!isSearchControlled) {
      setInternalSearch(next);
    }
    onSearchChange?.(event, { ...props, searchQuery: next });
    if (!isOpen) {
      setOpen(true, event);
    }
    setHighlightedIndex(0);
  };

  const activateHighlighted = (event: SyntheticEvent<HTMLElement>) => {
    const option =
      highlightedIndex >= 0
        ? visibleOptions[highlightedIndex]
        : keyboardSelection != null
          ? visibleOptions.find((o) => valuesEqual(o.value, keyboardSelection))
          : undefined;
    if (!option || option.divider || option.disabled) {
      return;
    }
    if (option.onClick) {
      option.onClick(event as MouseEvent<HTMLDivElement>, option);
    }
    if (option.value !== undefined) {
      selectOption(event, option.value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (disabled) {
      onKeyDown?.(event);
      return;
    }

    // Typing on the root (not the search input) should still drive search.
    if (
      search &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      event.key.length === 1 &&
      document.activeElement !== searchInputRef.current
    ) {
      if (!isOpen) {
        setOpen(true, event);
      }
      searchInputRef.current?.focus();
      const next = `${searchQuery}${event.key}`;
      if (!isSearchControlled) {
        setInternalSearch(next);
      }
      onSearchChange?.(event, { ...props, searchQuery: next });
      setHighlightedIndex(0);
      event.preventDefault();
      onKeyDown?.(event);
      return;
    }

    if (clearable && (event.key === "Backspace" || event.key === "Delete")) {
      const searchEmpty = !search || !searchQuery;
      if (searchEmpty) {
        event.preventDefault();
        clearValue(event);
      }
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) {
        setOpen(true, event);
      }
      setHighlightedIndex((prev) => {
        const next = findNextSelectableIndex(visibleOptions, prev, 1);
        const opt = visibleOptions[next];
        if (opt?.value != null) {
          setKeyboardSelection(opt.value);
        }
        return next;
      });
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        setOpen(true, event);
      }
      setHighlightedIndex((prev) => {
        const next = findNextSelectableIndex(visibleOptions, prev, -1);
        const opt = visibleOptions[next];
        if (opt?.value != null) {
          setKeyboardSelection(opt.value);
        }
        return next;
      });
    }

    if (isOpen && (event.key === "Enter" || event.key === " ")) {
      const target = event.target;
      const typingInSearch =
        Boolean(search) &&
        target instanceof HTMLElement &&
        (target === searchInputRef.current ||
          searchInputRef.current?.contains(target) ||
          target.closest("input.search, textarea"));
      // Allow spaces (and don't activate) while typing in the search field.
      if (event.key === " " && typingInSearch) {
        onKeyDown?.(event);
        return;
      }
      event.preventDefault();
      activateHighlighted(event);
    }

    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      setOpen(false, event);
    }

    onKeyDown?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    onBlur?.(event, props);
    const next = event.relatedTarget as Node | null;
    // Focus moved into the portaled menu — not a real blur of the control.
    if (next && refs.floating.current?.contains(next)) {
      return;
    }
  };

  const handleFocus = (event: FocusEvent<HTMLElement>) => {
    onFocus?.(event, props);
  };

  const handleRootClick = (event: MouseEvent<HTMLElement>) => {
    onClick?.(event, props);
  };

  const contextValue = useMemo(
    () => ({
      open: isOpen,
      setOpen,
      disabled: Boolean(disabled),
      direction: computed.direction,
      upward: computed.upward,
      value: value ?? undefined,
      multiple: isMultiple,
      closeOnChange,
      selectOption,
      getItemProps,
      rootProps: props,
    }),
    [
      closeOnChange,
      computed.direction,
      computed.upward,
      disabled,
      isOpen,
      isMultiple,
      props,
      selectOption,
      getItemProps,
      setOpen,
      value,
    ],
  );

  const hasValue = toArray(value).length > 0;

  const toggleFromCaret = (event: MouseEvent<Element>) => {
    // Search dropdowns use useClick({ toggle: false }) so the input can be
    // focused without closing. The caret should still toggle closed/open.
    event.preventDefault();
    event.stopPropagation();
    setOpen(!isOpen, event as MouseEvent<HTMLElement>);
  };

  const renderIcon = (): ReactNode => {
    if (trigger != null) {
      return null;
    }
    if (clearable && hasValue) {
      return (
        <Icon
          icon={XIcon}
          className="dropdown-clear"
          aria-label="Clear"
          onClick={(event) =>
            clearValue(event as unknown as SyntheticEvent<HTMLElement>)
          }
        />
      );
    }
    return (
      <Icon
        icon={ChevronDownIcon}
        className="dropdown-caret"
        aria-label={isOpen ? "Close" : "Open"}
        onClick={toggleFromCaret}
      />
    );
  };

  const renderLabels = (): ReactNode => {
    if (!isCombo) {
      return null;
    }
    return selectedOptions.map((opt) => (
      <Label
        key={String(opt.key ?? opt.value)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (opt.value !== undefined) {
            selectOption(e, opt.value);
          }
        }}
      >
        <LabelContent>{opt.text ?? String(opt.value)}</LabelContent>
        <LabelDetail>
          <StyledDropdownLabelRemove
            aria-label="Remove"
            role="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (opt.value !== undefined) {
                selectOption(e, opt.value);
              }
            }}
          >
            ×
          </StyledDropdownLabelRemove>
        </LabelDetail>
      </Label>
    ));
  };

  const optionsMenu =
    hasOptions && !useChildrenMenu ? (
      <DropdownMenu>
        {visibleOptions.length === 0 ? (
          <StyledDropdownMessage>{noResultsMessage}</StyledDropdownMessage>
        ) : (
          visibleOptions.map((option, index) => {
            const {
              key: optionKey,
              text: _text,
              icon: _icon,
              children: _children,
              divider,
              onClick: optionOnClick,
              ...itemProps
            } = option;
            if (divider) {
              return (
                <DropdownDivider
                  key={String(optionKey ?? `divider-${index}`)}
                />
              );
            }
            return (
              <DropdownItem
                key={String(optionKey ?? option.value ?? index)}
                {...itemProps}
                $highlighted={
                  highlightedIndex === index ||
                  (keyboardSelection != null &&
                    valuesEqual(option.value, keyboardSelection))
                }
                onClick={
                  optionOnClick
                    ? (event, data) => {
                        optionOnClick(event, { ...option, ...data });
                      }
                    : undefined
                }
              >
                {optionChildren(option)}
              </DropdownItem>
            );
          })
        )}
      </DropdownMenu>
    ) : null;

  const menuContent = useChildrenMenu ? childrenArray : optionsMenu;

  const triggerChildProps = isValidElement(trigger)
    ? (trigger.props as {
        children?: ReactNode;
        onClick?: (ev: MouseEvent) => void;
      })
    : null;

  const triggerEl =
    trigger && isValidElement(trigger)
      ? cloneElement(
          trigger as ReactElement<Record<string, unknown>>,
          {
            ref: triggerReferenceRef,
            ...(itemTrigger
              ? {
                  $dropdownTrigger: true,
                  children: (
                    <>
                      {triggerChildProps?.children}
                      <Icon
                        icon={submenuCaretIcon(
                          computed.direction,
                          computed.upward,
                        )}
                        className={cx(
                          "submenu-caret",
                          // Sit on the flyout side (not Semantic pointing).
                          computed.direction === "right" && "submenu-caret-end",
                        )}
                        aria-hidden
                      />
                    </>
                  ),
                }
              : null),
            ...getReferenceProps({
              onClick: (e: MouseEvent) => {
                triggerChildProps?.onClick?.(e);
                onClick?.(e as MouseEvent<HTMLElement>, props);
              },
            }),
          } as Partial<unknown>,
        )
      : null;

  const { isMounted, styles: transitionStyle } = useTransitionStyles(context, {
    duration: 200,
    initial: { opacity: 0, top: "-5px" },
    open: { opacity: 1, top: "0px" },
    close: { opacity: 0, top: "5px" },
  });

  const menuFloating = isMounted ? (
    <FloatingPortal root={getDefaultMountNode() ?? undefined}>
      <div
        ref={refs.setFloating}
        style={{ ...floatingStyles, ...transitionStyle, zIndex: 1900 }}
        {...getFloatingProps()}
      >
        {menuContent}
      </div>
    </FloatingPortal>
  ) : null;

  return (
    <FloatingNode id={nodeId}>
      <DropdownContext.Provider value={contextValue}>
        <>
          <StyledDropdown
            ref={rootReferenceRef as never}
            className={cx(
              className,
              trigger ? "is-trigger" : null,
              itemTrigger && "is-item-trigger",
              disabled && "is-disabled",
              isOutline && "is-outline",
              isOpen && "is-open",
              clearable && "is-clearable",
              search && "is-search",
              isMultiple && "is-multiple",
              isCombo && "is-combo",
              loading && "is-loading",
              fluid && "is-fluid",
              !isOutline && "is-inline",
            )}
            style={style}
            $variant={variant}
            $fluid={fluid}
            $combo={isCombo}
            $disabled={disabled}
            $size={size}
            tabIndex={
              trigger ? -1 : tabIndex !== undefined ? Number(tabIndex) : 0
            }
            aria-disabled={disabled || undefined}
            {...(trigger
              ? {}
              : getReferenceProps({
                  onClick: handleRootClick,
                  onKeyDown: handleKeyDown,
                  onBlur: handleBlur,
                  onFocus: handleFocus,
                }))}
            {...(rest as object)}
          >
            {triggerEl}
            {!trigger ? (
              <>
                {renderLabels()}
                {search ? (
                  <StyledDropdownSearch
                    ref={searchInputRef}
                    className="search"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!isOpen) {
                        setOpen(true, event);
                      }
                    }}
                    tabIndex={0}
                    aria-autocomplete="list"
                    autoComplete="off"
                    {...(searchInput as object)}
                  />
                ) : null}
                {(displayText != null || showPlaceholder) &&
                !hideTextForSearch ? (
                  <StyledDropdownText
                    className={cx(
                      showPlaceholder && !displayText && "is-default",
                    )}
                  >
                    {displayText ?? placeholder}
                  </StyledDropdownText>
                ) : null}
                {renderIcon()}
              </>
            ) : null}
          </StyledDropdown>
          {menuFloating}
        </>
      </DropdownContext.Provider>
    </FloatingNode>
  );
};

const DropdownWithTree = (props: DropdownComponentProps): ReactElement => {
  const parentId = useFloatingParentNodeId();
  // Root dropdowns own a FloatingTree so nested item-trigger menus can
  // coordinate (sibling open closes others). Nested dropdowns join the
  // ancestor tree via FloatingNode.
  if (parentId === null) {
    return (
      <FloatingTree>
        <DropdownComponent {...props} />
      </FloatingTree>
    );
  }
  return <DropdownComponent {...props} />;
};

export const Dropdown = styled(DropdownWithTree)``;
